import AHDate from "_shared/dates/date";
import {
  UNRECOGNIZED_STEP_STATUS,
  VendorRolloutBoardRowJSON,
  VendorRolloutBoardStageJSON,
  VendorRolloutBoardStepJSON,
} from "legal-entities/vendor-rollouts/_schema/vendor-rollout-board-schema";

/**
 * What the status board prints, kept out of the components that print it.
 *
 * Every rule here is a sentence about somebody's roll-out, so each one is worth a test of its own
 * rather than an assertion against rendered markup. Module-level functions also stay out of the
 * React Compiler's way entirely — nothing in a component can bail out over them.
 */

/** How a step reads, and how strongly. */
export type StepTone = "complete" | "pending" | "stopped" | "not-applicable" | "not-tracked" | "unknown";

export type StepDisplay = {
  text: string;
  tone: StepTone;
  /**
   * The sentence printed under the cell, when the step is waiting on somebody rather than on time.
   * Printed as text rather than shown on hover: it is the only place this reason appears outside
   * CloudWatch, and a reason a reader has to find is one most readers never see.
   */
  note?: string;
};

/**
 * The two steps of a stage, in the words the operations article uses for that stage.
 *
 * The API calls them "started" and "finished" because it describes every stage with one shape. A
 * client reading the board is looking for the words from the table they were shown at the 3P
 * meeting, so the labels are restated here.
 */
const STEP_LABELS: Record<string, { started: string; finished: string }> = {
  Invitation: { started: "Sent", finished: "Accepted" },
  AttorneyHubProposal: { started: "Sent", finished: "Accepted" },
  ClientProposal: { started: "Sent", finished: "Accepted" },
  FirstInvoice: { started: "Submitted", finished: "Validated" },
};

const STAGE_LABELS: Record<string, string> = {
  Invitation: "Invitation",
  AttorneyHubProposal: "Attorney Hub proposal",
  ClientProposal: "Client proposal",
  FirstInvoice: "First invoice",
};

const STARTING_STATE_LABELS: Record<string, string> = {
  NewToAttorneyHub: "New to Attorney Hub",
  AlreadyOnAttorneyHub: "Already on Attorney Hub",
};

/**
 * A name this bundle has no label for — a fifth stage, most likely, since the board renders the
 * stages the API sends rather than a list of its own. Splitting the name is a worse label than a
 * written one and a far better one than nothing.
 */
export function humanizeName(name: string): string {
  const spaced = name
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/([A-Z]+)([A-Z][a-z])/g, "$1 $2")
    .trim();
  return spaced.length > 0 ? spaced : name;
}

export function stageLabel(stageName: string): string {
  return STAGE_LABELS[stageName] ?? humanizeName(stageName);
}

export function stepLabels(stageName: string): { started: string; finished: string } {
  return STEP_LABELS[stageName] ?? { started: "Started", finished: "Finished" };
}

export function startingStateLabel(startingState: string): string {
  return STARTING_STATE_LABELS[startingState] ?? humanizeName(startingState);
}

/**
 * What one cell says.
 *
 * A completed step prints its date, because the date is the whole evidence that it happened; the
 * word "Completed" beside it would only take up room. A completed step that somehow carries no date
 * still says so rather than reading as pending.
 *
 * "Not applicable" and "Not tracked yet" are deliberately different sentences for what is, in the
 * payload, the same absence of a date. The first is about this firm — an established firm never
 * needed an invitation, and rendering that as pending would put a firm that is already working on a
 * list of laggards. The second is about the platform.
 *
 * A pending step carrying a reason says "Needs attention" instead, and prints the reason under it.
 * Both are true of the same cell — the step has not happened, and nothing will make it happen until
 * somebody acts — but only one of them tells the reader there is something to do. This is the only
 * place that reason is shown; without it the roll-out simply stops moving and says nothing.
 */
export function stepDisplay(step: VendorRolloutBoardStepJSON): StepDisplay {
  switch (step.status) {
    case "Completed":
      return step.occurredDate
        ? { text: new AHDate(step.occurredDate).asFormattedString, tone: "complete" }
        : { text: "Done", tone: "complete" };
    case "Pending":
      return step.stoppedReason
        ? { text: "Needs attention", tone: "stopped", note: step.stoppedReason }
        : { text: "Pending", tone: "pending" };
    case "NotApplicable":
      return { text: "Not applicable", tone: "not-applicable" };
    case "NotTracked":
      return { text: "—", tone: "not-tracked" };
    default:
      return { text: "—", tone: "unknown" };
  }
}

/** Whether a stage records nothing for anybody, and so is shown greyed rather than dropped. */
export function isStageNotTracked(stage: VendorRolloutBoardStageJSON): boolean {
  return stage.started.status === "NotTracked" && stage.finished.status === "NotTracked";
}

/** Whether a cell's status is one this bundle could not read. Kept for the tests that prove it. */
export function isUnrecognized(step: VendorRolloutBoardStepJSON): boolean {
  return step.status === UNRECOGNIZED_STEP_STATUS;
}

/**
 * The stages to draw columns for: every stage name any row carries, in the order first seen.
 *
 * Reading them off the rows rather than off a list held here is what lets the API add a stage
 * without a frontend deploy, and what keeps a row that is missing one from silently shifting its
 * remaining cells left under the wrong headings.
 */
export function boardStageNames(rows: readonly VendorRolloutBoardRowJSON[]): string[] {
  const names: string[] = [];
  for (const row of rows) {
    for (const stage of row.stages) {
      if (!names.includes(stage.name)) names.push(stage.name);
    }
  }
  return names;
}

/** The stage of this row with this name, or undefined when the row does not carry it. */
export function stageOf(
  row: VendorRolloutBoardRowJSON,
  stageName: string,
): VendorRolloutBoardStageJSON | undefined {
  return row.stages.find((stage) => stage.name === stageName);
}

export type NotTrackedNotice = {
  reason?: string | null;
  articleSlug?: string | null;
};

/**
 * The explanation for a stage nothing records, or undefined when the stage is live.
 *
 * Taken from the payload rather than written here: the reason a stage is untracked is the API's to
 * state, and a sentence duplicated in the bundle would go stale the day invoicing ships. The notice
 * is identical on every row, so the table prints it once, in the column heading.
 */
export function notTrackedNotice(
  rows: readonly VendorRolloutBoardRowJSON[],
  stageName: string,
): NotTrackedNotice | undefined {
  const stages = rows.map((row) => stageOf(row, stageName)).filter((stage) => stage !== undefined);
  if (stages.length === 0 || !stages.every(isStageNotTracked)) return undefined;

  const described = stages.find((stage) => stage.notTrackedReason || stage.notTrackedArticleSlug);
  return {
    reason: described?.notTrackedReason,
    articleSlug: described?.notTrackedArticleSlug,
  };
}

/**
 * When the board was read, phrased for a footer.
 *
 * The page says this because a stage that has happened but whose outbox row has not drained yet
 * reports as the stage before it. A client chasing a firm over the board deserves to know how fresh
 * it is.
 */
export function assembledAtLabel(assembledAt: string): string {
  const date = new AHDate(assembledAt);
  return date.isValid ? `As of ${date.format("D MMMM YYYY, h:mm a")}` : "As of an unknown time";
}
